// Commission statement → document HTML.
//
// The statement an agent prints is a COMPANY document, not a screenshot of the app:
// HQ letterhead from document_settings (the same row the co-agency letters use), a
// reconciliation an accountant would recognise, itemised sections, and a footer that
// says who generated it and when. It renders at a fixed A4-ish width inside DocViewer
// and prints through the browser's own dialog, so "Save as PDF" produces a page of
// paper rather than a 390px phone column.
//
// Kept separate from the screen for the same reason buildLetterHtml is: the paper and
// the phone have different jobs, and trying to make one DOM serve both is what made
// the old statement print with cut-off columns.
import { escapeHtml } from './coAgencyLetterDoc';
import { letterheadHtml } from './documentLetterhead';

const NAVY = '#241854';
const INK = '#14181f';
const MUTED = '#5b6270';
const RULE = '#d9dce4';
const FAINT = '#f4f6fa';

const money = (n) => `RM ${(Number(n) || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// Deductions read as accountants write them, so the reconciliation column scans.
const less = (n) => `(${money(n)})`;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const docDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return `${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

/**
 * A stable reference for the period, so an agent and HQ can name the same statement
 * in an email. Deliberately derived (REN + period) rather than a serial: nothing is
 * stored, and a statement is a view of the ledger, not a numbered instrument.
 */
export const statementRef = (st) => {
  const ren = String(st?.agent?.ren_no || '').replace(/^REN\s*/i, '').trim() || '—';
  const from = st?.period?.from;
  const to = st?.period?.to;
  const span = from && to
    ? (from.slice(0, 4) === to.slice(0, 4)
      ? (from.slice(5, 7) === '01' && to.slice(5, 7) === '12' ? from.slice(0, 4) : `${from.slice(0, 4)}-${from.slice(5, 7)}`)
      : `${from.slice(0, 4)}-${to.slice(0, 4)}`)
    : 'ALL';
  return `CS/${ren}/${span}`;
};

export const statementFileBase = (st) =>
  `cid-commission-statement-${(st?.period?.label || 'all-time').replace(/[^\w]+/g, '-').toLowerCase()}`;

const cell = (v, opts = {}) => {
  const { align = 'left', bold = false, muted = false, wrap = false } = opts;
  return `<td style="padding:7px 9px;border-bottom:1px solid #eef0f4;text-align:${align};vertical-align:top;${wrap ? '' : 'white-space:nowrap;'}${bold ? 'font-weight:600;' : ''}${muted ? `color:${MUTED};` : ''}font-variant-numeric:tabular-nums">${v}</td>`;
};

const table = (heads, rows, foot) => `
  <table style="width:100%;border-collapse:collapse;font-size:11px;margin-top:8px">
    <thead>
      <tr>${heads.map((h) => `<th style="padding:7px 9px;text-align:${h.align || 'left'};border-bottom:1.5px solid ${RULE};color:${MUTED};font-weight:600;font-size:10px;letter-spacing:.04em;text-transform:uppercase;white-space:nowrap">${escapeHtml(h.label)}</th>`).join('')}</tr>
    </thead>
    <tbody>${rows.join('')}</tbody>
    ${foot ? `<tfoot><tr>${foot}</tr></tfoot>` : ''}
  </table>`;

const section = (n, title, inner) => `
  <div style="margin-top:20px;break-inside:avoid">
    <div style="display:flex;align-items:baseline;gap:8px;border-bottom:1px solid ${RULE};padding-bottom:5px">
      <span style="font-size:10px;font-weight:700;color:#fff;background:${NAVY};border-radius:3px;padding:2px 6px">${n}</span>
      <span style="font-size:11.5px;font-weight:700;letter-spacing:.06em;color:${NAVY};text-transform:uppercase">${escapeHtml(title)}</span>
    </div>
    ${inner}
  </div>`;

/** One reconciliation line in the summary block. */
const recon = (label, value, opts = {}) => {
  const { strong = false, rule = false, dim = false } = opts;
  return `
    <div style="display:flex;justify-content:space-between;gap:16px;padding:${strong ? '9px 0 2px' : '5px 0'};${rule ? `border-top:1px solid ${RULE};` : ''}">
      <span style="font-size:${strong ? 12 : 11.5}px;${strong ? 'font-weight:700;' : ''}color:${dim ? MUTED : INK}">${escapeHtml(label)}</span>
      <span style="font-size:${strong ? 13 : 11.5}px;font-weight:${strong ? 700 : 600};color:${dim ? MUTED : INK};font-variant-numeric:tabular-nums;white-space:nowrap">${value}</span>
    </div>`;
};

/**
 * `st` is GET /earnings/statement, `s` the document_settings row (letterhead).
 * `generatedAt` is passed in so the caller controls the timestamp.
 */
export function buildStatementHtml(st, s = {}, generatedAt = new Date()) {
  const totals = st?.totals || {};
  const earned = Number(totals.entitled) || 0;
  const released = Number(totals.released) || 0;
  const paid = Number(totals.paid) || 0;
  const locked = Math.max(0, earned - released);
  const balance = released - paid;

  const commissions = st?.commissions || [];
  const overrides = st?.overrides || [];
  const payouts = st?.payouts || [];

  const company = s.company_name || 'CID Realtors';

  // Same letterhead every other CID document prints, drawn in the statement's own
  // navy/grey palette rather than flat black so it doesn't fight the page below it.
  const letterhead = letterheadHtml(
    { ...s, company_name: company },
    { colors: { text: NAVY, muted: MUTED } },
  );

  const title = `
    <div style="margin-top:16px;border-top:2px solid ${NAVY};border-bottom:1px solid ${RULE};padding:12px 0">
      <div style="display:flex;justify-content:space-between;align-items:flex-end;gap:20px">
        <div>
          <div style="font-size:19px;font-weight:700;letter-spacing:.02em;color:${NAVY}">COMMISSION STATEMENT</div>
          <div style="font-size:10.5px;color:${MUTED};margin-top:3px">Reference ${escapeHtml(statementRef(st))} &nbsp;·&nbsp; Issued ${escapeHtml(docDate(generatedAt))}</div>
        </div>
        <div style="text-align:right">
          <div style="font-size:9.5px;font-weight:700;letter-spacing:.08em;color:${MUTED}">STATEMENT PERIOD</div>
          <div style="font-size:12.5px;font-weight:700;margin-top:2px">${escapeHtml(st?.period?.label || 'All time')}</div>
        </div>
      </div>
    </div>`;

  const parties = `
    <div style="display:flex;gap:14px;margin-top:14px">
      <div style="flex:1;background:${FAINT};border-radius:6px;padding:11px 13px">
        <div style="font-size:9.5px;font-weight:700;letter-spacing:.08em;color:${MUTED}">STATEMENT FOR</div>
        <div style="font-size:13px;font-weight:700;margin-top:3px">${escapeHtml(st?.agent?.name || '—')}</div>
        <div style="font-size:10.5px;color:${MUTED};margin-top:1px">
          ${st?.agent?.ren_no ? `REN ${escapeHtml(String(st.agent.ren_no).replace(/^REN\s*/i, ''))}` : ''}${st?.agent?.tier ? ` &nbsp;·&nbsp; ${escapeHtml(String(st.agent.tier))}% tier` : ''}
        </div>
      </div>
      <div style="flex:1;background:${FAINT};border-radius:6px;padding:11px 13px">
        <div style="font-size:9.5px;font-weight:700;letter-spacing:.08em;color:${MUTED}">THIS PERIOD</div>
        <div style="font-size:13px;font-weight:700;margin-top:3px">${commissions.length} ${commissions.length === 1 ? 'deal' : 'deals'}</div>
        <div style="font-size:10.5px;color:${MUTED};margin-top:1px">${payouts.length} ${payouts.length === 1 ? 'disbursement' : 'disbursements'}${overrides.length ? ` &nbsp;·&nbsp; ${overrides.length} override ${overrides.length === 1 ? 'line' : 'lines'}` : ''}</div>
      </div>
    </div>`;

  // The reconciliation is what makes this a statement rather than a list: every
  // figure below is one of these four states, and they add up.
  const summary = `
    <div style="margin-top:16px;border:1px solid ${RULE};border-radius:8px;padding:13px 15px;break-inside:avoid">
      <div style="font-size:10px;font-weight:700;letter-spacing:.08em;color:${MUTED};margin-bottom:4px">SUMMARY</div>
      ${recon('Commission earned in this period', money(earned))}
      ${recon('Less — not yet released by collection', less(locked), { dim: true })}
      ${recon('Released to your ledger', money(released), { rule: true })}
      ${recon('Less — disbursed to you', less(paid), { dim: true })}
      ${recon('Balance held for you', money(balance), { strong: true, rule: true })}
    </div>`;

  const commissionSection = section(1, `Commission by sale (${commissions.length})`,
    commissions.length === 0
      ? `<div style="font-size:11px;color:${MUTED};padding:10px 2px">No commission was recorded in this period.</div>`
      : table(
        [{ label: 'Sale' }, { label: 'Date' }, { label: 'Property' }, { label: 'Earned', align: 'right' }, { label: 'Released', align: 'right' }, { label: 'Paid', align: 'right' }],
        commissions.map((c) => `<tr style="break-inside:avoid">${
          cell(`<b>${escapeHtml(c.sale_ref || '—')}</b>`)
        }${cell(escapeHtml(docDate(c.sale_date)), { muted: true })
        }${cell(escapeHtml(c.property || '—'), { wrap: true, muted: true })
        }${cell(money(c.entitled), { align: 'right', bold: true })
        }${cell(money(c.released), { align: 'right' })
        }${cell(money(c.paid), { align: 'right' })}</tr>`),
        `<td colspan="3" style="padding:8px 9px;font-size:11px;font-weight:700;border-top:1.5px solid ${RULE}">Subtotal</td>
         <td style="padding:8px 9px;text-align:right;font-size:11px;font-weight:700;border-top:1.5px solid ${RULE};white-space:nowrap;font-variant-numeric:tabular-nums">${money(earned)}</td>
         <td style="padding:8px 9px;text-align:right;font-size:11px;font-weight:700;border-top:1.5px solid ${RULE};white-space:nowrap;font-variant-numeric:tabular-nums">${money(released)}</td>
         <td style="padding:8px 9px;text-align:right;font-size:11px;font-weight:700;border-top:1.5px solid ${RULE};white-space:nowrap;font-variant-numeric:tabular-nums">${money(paid)}</td>`
      ));

  const overrideSection = overrides.length === 0 ? '' : section(2, `Override income (${overrides.length})`,
    table(
      [{ label: 'Sale' }, { label: 'From your team' }, { label: 'Generation' }, { label: 'Amount', align: 'right' }],
      overrides.map((o) => `<tr>${cell(`<b>${escapeHtml(o.sale_ref || '—')}</b>`)}${cell(escapeHtml(o.from || '—'), { wrap: true })}${cell(`Gen ${escapeHtml(String(o.gen ?? 1))}`, { muted: true })}${cell(money(o.amount), { align: 'right', bold: true })}</tr>`),
      `<td colspan="3" style="padding:8px 9px;font-size:11px;font-weight:700;border-top:1.5px solid ${RULE}">Subtotal</td>
       <td style="padding:8px 9px;text-align:right;font-size:11px;font-weight:700;border-top:1.5px solid ${RULE};white-space:nowrap;font-variant-numeric:tabular-nums">${money(totals.override)}</td>`
    ));

  const payoutNo = overrides.length === 0 ? 2 : 3;
  const payoutSection = section(payoutNo, `Disbursements (${payouts.length})`,
    payouts.length === 0
      ? `<div style="font-size:11px;color:${MUTED};padding:10px 2px">No disbursement was made in this period.</div>`
      : table(
        [{ label: 'Date' }, { label: 'Cheque / reference' }, { label: 'Payment voucher' }, { label: 'Status' }, { label: 'Amount', align: 'right' }],
        payouts.map((p) => `<tr>${cell(escapeHtml(docDate(p.payout_date)))}${cell(escapeHtml(p.cheque_number || '—'))}${cell(escapeHtml(p.voucher_no || '—'))}${cell(escapeHtml(p.status || '—'), { muted: true })}${cell(money(p.amount), { align: 'right', bold: true })}</tr>`),
        `<td colspan="4" style="padding:8px 9px;font-size:11px;font-weight:700;border-top:1.5px solid ${RULE}">Total disbursed</td>
         <td style="padding:8px 9px;text-align:right;font-size:11px;font-weight:700;border-top:1.5px solid ${RULE};white-space:nowrap;font-variant-numeric:tabular-nums">${money(totals.payouts)}</td>`
      ));

  const notes = `
    <div style="margin-top:22px;border-top:1px solid ${RULE};padding-top:10px;break-inside:avoid">
      <div style="font-size:9.5px;font-weight:700;letter-spacing:.08em;color:${MUTED};margin-bottom:5px">NOTES</div>
      <ul style="margin:0;padding-left:15px;font-size:9.5px;color:${MUTED};line-height:1.65">
        <li><b>Earned</b> is your share of a deal, fixed when HQ raises the first invoice.</li>
        <li><b>Released</b> is the part freed as the company collects from the purchaser; the rest stays locked until collection.</li>
        <li><b>Balance held</b> is released commission not yet disbursed. It is paid on the next payout run.</li>
        <li>Figures cover this agent's own commission lines for the stated period only. This statement is informational and is not a tax document or an invoice.</li>
      </ul>
      <div style="display:flex;justify-content:space-between;gap:16px;margin-top:12px;padding-top:8px;border-top:1px solid ${RULE};font-size:9px;color:${MUTED}">
        <span>${escapeHtml(company)} &nbsp;·&nbsp; ${escapeHtml(statementRef(st))} &nbsp;·&nbsp; ${escapeHtml(st?.agent?.name || '')}</span>
        <span>Computer-generated on ${escapeHtml(docDate(generatedAt))} — no signature required.</span>
      </div>
    </div>`;

  // On paper it follows every other CID document (the servers' styles.hbs): 15mm on
  // every side of every page drawn as cloned padding with no page margin, so the
  // browser has nowhere to print its date / URL / page number, and DocViewer's 800px
  // on-screen wrapper released so the sheet is not shrunk onto A4 (which alone
  // prints it ~13% larger than before). Unlike the letters it is NOT zoomed 7/6:
  // its commission table needs ~635px for the sale, date and three RM columns,
  // which never wrap, and A4 less 30mm leaves 681px — at 7/6 only 584px, so the
  // table ran past the right margin and squeezed the property column to a sliver.
  // Safari, which cannot clone a block's padding, gets a 15mm page margin instead.
  return `
    <style>
      @page { size: A4; margin: 15mm; }
      @supports (box-decoration-break: clone) { @page { margin: 0; } }
      .cid-stmt { width: 800px; max-width: 100%; margin: 0 auto; padding: 26px 30px 30px;
        background: #fff; color: ${INK}; box-sizing: border-box;
        font-family: 'Times New Roman', Times, serif; }
      /* The one typeface every CID document prints in (owner, 2026-10-08). */
      .cid-stmt * { font-family: inherit; }
      .cid-stmt table { page-break-inside: auto; }
      .cid-stmt thead { display: table-header-group; }
      .cid-stmt tr { page-break-inside: avoid; }
      @media print {
        html, body { margin: 0 !important; padding: 0 !important; }
        .doc-viewer-page { width: auto !important; }
        .cid-stmt { width: auto; max-width: none; padding: 0; }
        @supports (box-decoration-break: clone) {
          .cid-stmt { padding: 15mm; box-decoration-break: clone; }
        }
      }
    </style>
    <div class="cid-stmt">
      ${letterhead}
      ${title}
      ${parties}
      ${summary}
      ${commissionSection}
      ${overrideSection}
      ${payoutSection}
      ${notes}
    </div>`;
}
